import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?limit=100`);
      setBlogs(response.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const cats = [...new Set(blogs.map(b => b.category))];
    return ['All', ...cats];
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    if (activeCategory === 'All') return blogs;
    return blogs.filter(b => b.category === activeCategory);
  }, [blogs, activeCategory]);

  return (
    <div className="bg-background py-24 md:py-32" data-testid="blogs-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Our Insights</p>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight font-playfair mb-6" data-testid="blogs-title">
            Tax, GST & Financial Planning Services in Indore
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl mx-auto">
            Stay updated with the latest news, tips, and insights on income tax, GST, and financial planning.
          </p>
        </div>

        {/* Category Filter */}
        {!loading && blogs.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="blog-category-filter">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={activeCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory(cat)}
                className={activeCategory === cat ? 'bg-secondary hover:bg-secondary/90' : ''}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
                {cat !== 'All' && (
                  <span className="ml-1.5 text-xs opacity-70">
                    ({blogs.filter(b => b.category === cat).length})
                  </span>
                )}
              </Button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">Loading blogs...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <Card key={blog.id || index} className="group hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={`blog-${index}`}>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={blog.image_url || 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80'}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-xs text-secondary mb-3">
                      <span className="px-2 py-1 bg-secondary/10 rounded">{blog.category}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold font-playfair mb-3 group-hover:text-secondary transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{blog.excerpt}</p>
                    <Link to={`/blogs/${blog.slug}`} className="text-sm font-medium text-secondary hover:underline inline-flex items-center gap-1">
                      Read More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            {filteredBlogs.length === 0 && (
              <p className="text-center text-muted-foreground py-12">No articles found in this category.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blogs;
